import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { UploadCloud, Link2, Youtube, Video as VideoIcon, Loader2, Plus, CheckCircle2, Download, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { uploadFile, createVideo, createCategory, resolveImportUrl, updateChannel, getChannel, listMedia, myStorage, resolveFileUrl } from '../lib/api';
import { detectVideoSource, SOURCE_LABELS } from '../lib/sources';
import StorageMeter from '../components/StorageMeter';

export default function Upload() {
  const { user, categories, settings, refreshCategories } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState('file');
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [cat, setCat] = useState('trending');
  const [type, setType] = useState('video');
  const [visibility, setVisibility] = useState('public');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState(null);
  const [thumbUrl, setThumbUrl] = useState('');
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [detected, setDetected] = useState(null);   // parsed source
  const [detectErr, setDetectErr] = useState('');
  const [channel, setChannel] = useState(null);
  const [downloadable, setDownloadable] = useState(false);
  const [rightsOk, setRightsOk] = useState(false);
  const [storage, setStorage] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [library, setLibrary] = useState([]);

  const canCreateCategory = settings?.allow_custom_categories || user?.role === 'admin' || user?.role === 'owner';

  // Load channel + storage + media library
  useEffect(() => {
    if (!user?.channel_id) return;
    getChannel(user.channel_id).then(ch => {
      setChannel(ch);
      if (ch?.downloads_default) setDownloadable(true);
    }).catch(() => {});
  }, [user]);
  useEffect(() => { myStorage().then(setStorage).catch(() => {}); }, []);
  useEffect(() => {
    if (!showPicker) return;
    listMedia().then(setLibrary).catch(() => {});
  }, [showPicker]);

  // Live URL detection
  useEffect(() => {
    if (tab === 'file' || !url.trim()) { setDetected(null); setDetectErr(''); return; }
    const local = detectVideoSource(url);
    if (local) { setDetected(local); setDetectErr(''); if (local.thumb && !thumbUrl) setThumbUrl(local.thumb); return; }
    // Fall back to server resolver (handles edge cases)
    const t = setTimeout(async () => {
      try { const s = await resolveImportUrl(url); setDetected(s); setDetectErr(''); if (s.thumb && !thumbUrl) setThumbUrl(s.thumb); }
      catch (err) { setDetected(null); setDetectErr(err?.response?.data?.detail || 'Unsupported URL'); }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [url, tab]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('video/')) { toast.error('Please select a video file'); return; }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^/.]+$/, ''));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) { toast.error('Title is required'); return; }
    if (!user?.channel_id) { toast.error('Only creator accounts can upload. Admin accounts lack a channel.'); return; }
    if (tab !== 'file' && !rightsOk) { toast.error('You must confirm you have rights to this content'); return; }

    setBusy(true);
    try {
      let source;
      if (tab === 'file') {
        if (!file) { toast.error('Choose a video file'); return; }
        const { url: fileUrl } = await uploadFile(file, setProgress);
        source = { type: 'file', url: fileUrl };
      } else {
        if (!detected) { toast.error('Paste a supported URL'); return; }
        source = detected;
      }

      const payload = {
        title: title.trim(),
        desc: desc.trim(),
        category: cat,
        type,
        visibility,
        thumb: thumbUrl || '',
        source,
        tags: [],
        duration: type === 'live' ? 'LIVE' : '—',
        downloadable: !!downloadable,
        rights_confirmed: tab !== 'file' ? !!rightsOk : true,
      };
      const created = await createVideo(payload);
      toast.success('Video published');
      nav(`/watch/${created.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setBusy(false); setProgress(0); }
  };

  const toggleChannelDefault = async (v) => {
    try {
      const c = await updateChannel(user.channel_id, { downloads_default: v });
      setChannel(c); setDownloadable(v);
      toast.success(v ? 'Downloads ON by default for your new uploads' : 'Downloads OFF by default');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="max-w-[980px] mx-auto" data-testid="upload-page">
      <div>
        <div className="chip">Upload</div>
        <h1 className="font-display text-[32px] md:text-[40px] mt-3 leading-none">Publish a new video</h1>
        <p className="text-white/60 text-[14px] mt-1">Upload from your device, or import from YouTube, Vimeo, Google Drive, Dropbox, HLS, and more.</p>
      </div>

      {storage && <div className="mt-6"><StorageMeter data={storage} /></div>}

      <div className="card-shell p-6 mt-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white/[0.04] border border-white/10">
            <TabsTrigger value="file" data-testid="tab-file"><UploadCloud className="w-4 h-4 mr-2" /> File upload</TabsTrigger>
            <TabsTrigger value="url" data-testid="tab-url"><Link2 className="w-4 h-4 mr-2" /> Import from URL</TabsTrigger>
          </TabsList>

          <form onSubmit={submit} className="mt-6 grid lg:grid-cols-[1.2fr_1fr] gap-6">
            <div className="space-y-4">
              <TabsContent value="file" className="m-0">
                <label className="block border-2 border-dashed border-white/15 rounded-xl p-8 text-center hover:border-[#ff2e63]/60 transition-colors cursor-pointer">
                  <input type="file" accept="video/*" className="hidden" onChange={pickFile} data-testid="file-input" />
                  <UploadCloud className="w-10 h-10 mx-auto text-white/50" />
                  <div className="mt-3 font-semibold">Drag and drop or click to select</div>
                  <div className="text-[12.5px] text-white/50 mt-1">MP4, MOV, WEBM • up to 2 GB on VidCreForge+</div>
                  {file && <div className="mt-3 text-[13px] text-[#22d3ee]">{file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB</div>}
                </label>
                {busy && tab === 'file' && (
                  <div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full" style={{ width: progress + '%', background: 'linear-gradient(90deg,#ff2e63,#22d3ee)', transition: 'width 0.2s linear' }} />
                    </div>
                    <div className="text-[12px] text-white/60 mt-1">Uploading… {Math.round(progress)}%</div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="url" className="m-0">
                <label className="block">
                  <span className="text-[13px] text-white/70 flex items-center gap-1.5"><Youtube className="w-3.5 h-3.5" /> Video URL</span>
                  <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://youtube.com/watch?v=… / drive.google.com/file/d/… / …m3u8"
                         className="mt-1 input-field w-full" data-testid="import-url-input" />
                </label>
                {detected && (
                  <div className="mt-2 card-shell p-3 flex items-center gap-2" data-testid="detect-ok">
                    <CheckCircle2 className="w-4 h-4 text-[#22d3ee]" />
                    <div className="text-[13px]">Detected: <span className="font-semibold text-[#22d3ee]">{SOURCE_LABELS[detected.type] || detected.type}</span>
                      {detected.id && <span className="text-white/50"> • id {detected.id}</span>}
                    </div>
                    <span className="ml-auto text-[11px] text-white/45 uppercase tracking-wider">streams only</span>
                  </div>
                )}
                {detectErr && url && (
                  <div className="mt-2 card-shell p-3 flex items-start gap-2 border-[#ff6b8a]/40" data-testid="detect-err">
                    <AlertCircle className="w-4 h-4 text-[#ff6b8a] mt-0.5" />
                    <div className="text-[13px] text-white/80">{detectErr}</div>
                  </div>
                )}
                <div className="mt-3 text-[11.5px] text-white/45 leading-relaxed">
                  Supported: YouTube, Vimeo, Google Drive, Dropbox, Dailymotion, Twitch, Facebook, HLS (.m3u8), and direct .mp4/.webm links. VidCreForge only stores the link — no file bytes are copied until a viewer actually downloads.
                </div>
              </TabsContent>

              <label className="block">
                <span className="text-[13px] text-white/70">Title</span>
                <input value={title} onChange={e => setTitle(e.target.value)} placeholder="A catchy title…" className="mt-1 input-field w-full" data-testid="title-input" />
              </label>
              <label className="block">
                <span className="text-[13px] text-white/70">Description</span>
                <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={4} placeholder="Tell viewers about your video…" className="mt-1 input-field w-full" />
              </label>
              <label className="block">
                <span className="text-[13px] text-white/70">Custom thumbnail URL (optional)</span>
                <div className="mt-1 flex gap-2">
                  <input value={thumbUrl} onChange={e => setThumbUrl(e.target.value)} placeholder="https://…" className="input-field flex-1" />
                  <button type="button" onClick={() => setShowPicker(true)} className="btn-ghost" data-testid="open-media-picker">
                    <ImageIcon className="w-4 h-4" /> Browse library
                  </button>
                </div>
              </label>

              {tab === 'url' && (
                <label className="flex items-start gap-2 card-shell p-3 cursor-pointer" data-testid="rights-checkbox-wrap">
                  <input type="checkbox" checked={rightsOk} onChange={e => setRightsOk(e.target.checked)} className="mt-0.5" data-testid="rights-checkbox" />
                  <span className="text-[13px] text-white/80">I own or have the rights to upload and distribute this content. I understand VidCreForge removes infringing content on DMCA notice.</span>
                </label>
              )}
            </div>

            <div className="space-y-4">
              <label className="block">
                <span className="text-[13px] text-white/70 flex items-center justify-between">
                  <span>Category</span>
                  {canCreateCategory && (
                    <button type="button" onClick={() => setShowNewCat(v => !v)} className="text-[12px] text-[#ff6b8a] hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> New</button>
                  )}
                </span>
                <Select value={cat} onValueChange={setCat}>
                  <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141418] border-white/10 text-white">
                    {(categories || []).map(c => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {showNewCat && canCreateCategory && (
                  <div className="mt-2 flex gap-2">
                    <input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="New category name" className="flex-1 input-field" />
                    <button type="button" onClick={async () => {
                      if (!newCatName.trim()) return;
                      try { const c = await createCategory(newCatName.trim()); await refreshCategories(); setCat(c.slug); setNewCatName(''); setShowNewCat(false); toast.success('Category added'); }
                      catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
                    }} className="btn-ghost">Add</button>
                  </div>
                )}
              </label>
              <label className="block">
                <span className="text-[13px] text-white/70">Type</span>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141418] border-white/10 text-white">
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="short">Short (vertical)</SelectItem>
                    <SelectItem value="movie">Movie</SelectItem>
                    <SelectItem value="course">Course</SelectItem>
                    <SelectItem value="live">Live</SelectItem>
                  </SelectContent>
                </Select>
              </label>
              <label className="block">
                <span className="text-[13px] text-white/70">Visibility</span>
                <Select value={visibility} onValueChange={setVisibility}>
                  <SelectTrigger className="mt-1 bg-white/[0.04] border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#141418] border-white/10 text-white">
                    <SelectItem value="public">Public — Free</SelectItem>
                    <SelectItem value="unlisted">Unlisted</SelectItem>
                    <SelectItem value="premium">Premium — Members only</SelectItem>
                    <SelectItem value="ppv">Pay-Per-View</SelectItem>
                  </SelectContent>
                </Select>
              </label>

              <div className="card-shell p-4">
                <div className="flex items-center gap-2 text-[#22d3ee] text-[12px] uppercase tracking-wider"><Download className="w-3.5 h-3.5" /> Downloads</div>
                <div className="mt-2 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[13.5px] font-semibold">Allow download of this video</div>
                      <div className="text-[12px] text-white/55">Requires viewers to have a VidCreForge+ plan.</div>
                    </div>
                    <Switch checked={downloadable} onCheckedChange={setDownloadable} data-testid="video-downloadable-switch" />
                  </div>
                  <div className="flex items-start justify-between gap-3 pt-3 border-t border-white/5">
                    <div>
                      <div className="text-[13.5px] font-semibold">Default for all my new uploads</div>
                      <div className="text-[12px] text-white/55">Your channel-level preference.</div>
                    </div>
                    <Switch checked={!!channel?.downloads_default} onCheckedChange={toggleChannelDefault} data-testid="channel-downloads-default-switch" />
                  </div>
                  {settings?.allow_downloads === false && (
                    <div className="text-[11.5px] text-[#ffcf52] border border-[#ffcf52]/30 bg-[#ffcf52]/10 rounded-lg p-2">
                      Platform-wide downloads are currently OFF (Owner setting). Your toggle will apply once re-enabled.
                    </div>
                  )}
                </div>
              </div>

              <div className="card-shell p-4">
                <div className="text-[12px] text-white/55 uppercase tracking-wider">Preview</div>
                <div className="mt-2 thumb">
                  <img src={thumbUrl || detected?.thumb || 'https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=900'} alt="preview" />
                </div>
                <div className="mt-2 text-[13px] font-semibold line-clamp-2">{title || 'Your video title'}</div>
                <div className="text-[12px] text-white/55">{user?.name}</div>
              </div>

              <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-60" data-testid="submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <VideoIcon className="w-4 h-4" />} {busy ? 'Publishing…' : 'Publish video'}
              </button>
            </div>
          </form>
        </Tabs>
      </div>

      {showPicker && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={() => setShowPicker(false)} data-testid="media-picker-overlay">
          <div className="w-full max-w-[960px] max-h-[86vh] overflow-y-auto bg-[#121217] border border-white/10 rounded-2xl shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
              <div className="font-display text-[22px]">Pick a thumbnail</div>
              <button onClick={() => setShowPicker(false)} className="text-white/60 hover:text-white text-[13px]" data-testid="media-picker-close">Close</button>
            </div>
            <div className="p-5">
              {library.length === 0 ? (
                <div className="p-10 text-center text-white/55 text-[14px]">
                  No images yet — <a href="/media" className="text-[#ff6b8a] underline">head to Media Library</a> to upload your first image.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {library.map(m => (
                    <button key={m.id} onClick={() => { setThumbUrl(resolveFileUrl(m.url)); setShowPicker(false); toast.success('Thumbnail selected'); }}
                      className="card-shell overflow-hidden text-left hover:ring-2 hover:ring-[#ff2e63]/60 transition"
                      data-testid={`picker-item-${m.id}`}>
                      <div className="aspect-video bg-black"><img src={resolveFileUrl(m.url)} alt={m.name} className="w-full h-full object-cover" loading="lazy" /></div>
                      <div className="p-2 text-[12.5px] truncate">{m.name}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
